import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import "./App.css";
import { App } from "./App";
import "./i18n/config.js";
import {
  AccountInfo,
  AuthenticationResult,
  EventMessage,
  EventType,
  PublicClientApplication,
} from "@azure/msal-browser";
import { MsalProvider } from "@azure/msal-react";
import { msalConfig } from "./authConfig";
import { initializeMsalInstance } from "./services/authUtility";
import settings from "./appSettings";
import config from "./config";

/**
 * Initialize a PublicClientApplication instance which is provided to the MsalProvider component
 * We recommend initializing this outside of your root component to ensure it is not re-initialized on re-renders
 */
const msalInstance = new PublicClientApplication(msalConfig(settings.tenantId, config.web.baseUrl));
// Account selection logic is app dependent. Adjust as needed for different use cases.

const setActiveAccount = (account: AccountInfo) => {
  msalInstance.setActiveAccount(account);
  const activeEmail = msalInstance.getActiveAccount()?.username;
  console.info('active account set', activeEmail);
};

msalInstance.initialize().then(() => {
  const accounts = msalInstance.getAllAccounts();

  if (accounts.length > 0) {
    // Prefer the account from the app's own tenant to avoid picking
    // a calendar-connected account that was added via /common redirect.
    const tenantAccount = accounts.find(a => a.tenantId === settings.tenantId);
    if (tenantAccount) {
      setActiveAccount(tenantAccount);
    } else {
      msalInstance.setActiveAccount(undefined);
    }
  }

  msalInstance.addEventCallback((event: EventMessage) => {
    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
      const payload = event.payload as AuthenticationResult;
      if (payload?.account?.username) {
        payload.account.username = payload.account.username.toLowerCase();
      }
      const account = payload.account;
      // Only set active account for the app's own tenant.
      // Calendar-connected accounts from /common authority should NOT
      // become the app's active account.
      if (account?.tenantId === settings.tenantId) {
        setActiveAccount(account);
      }
      // Clear highlighted items
      window.sessionStorage.setItem("actorHighlightedItems", "[]");
      window.sessionStorage.setItem("creatorHighlightedItems", "[]");
    } else if (event.eventType === EventType.LOGOUT_SUCCESS && event.payload) {
      msalInstance.setActiveAccount(undefined);
    }
  });

  //set this msal instance in functional context (for token generation in services)
  initializeMsalInstance(msalInstance);

  const container = document.getElementById("root");
  if (!container) {
    throw new Error("Root container element '#root' was not found in the document.");
  }
  const root = createRoot(container);
  root.render(
    <StrictMode>
      <MsalProvider instance={msalInstance}>
        <App />
      </MsalProvider>
    </StrictMode>
  );

  // Performance measuring via reportWebVitals was removed (dead code — no handler was wired up).
});
